import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar            from './components/Navbar';
import Footer            from './components/Footer';
import PWAInstallBanner  from './components/PWAInstallBanner';
import WhatsAppFloat     from './components/WhatsAppFloat';
import ScrollTop         from './components/ScrollTop';
import MobileBottomNav   from './components/MobileBottomNav';

// Public pages
import Home      from './pages/Home';
import About     from './pages/About';
import Services  from './pages/Services';
import Portfolio from './pages/Portfolio';
import Careers   from './pages/Careers';
import Contact   from './pages/Contact';
import GetQuote  from './pages/GetQuote';
import Reviews   from './pages/Reviews';
import FAQ       from './pages/FAQ';

import { Blog, BlogPost }                             from './pages/Blog';
import { PrivacyPolicy, TermsAndConditions, Sitemap } from './pages/StaticPages';

// Service sub-pages
import CompleteModular from './pages/services/CompleteModular';
import InteriorDesign  from './pages/services/InteriorDesign';
import HomeLighting    from './pages/services/HomeLighting';
import ModularKitchen  from './pages/services/ModularKitchen';
import FalseCeiling    from './pages/services/FalseCeiling';
import AluminiumGlass  from './pages/services/AluminiumGlass';

// Admin - core
import AdminLogin        from './admin/AdminLogin';
import AdminLayout       from './admin/AdminLayout';
import AdminDashboard    from './admin/AdminDashboard';
import AdminBanners      from './admin/AdminBanners';
import AdminPortfolio    from './admin/AdminPortfolio';
import AdminCareers      from './admin/AdminCareers';
import AdminContacts     from './admin/AdminContacts';
import AdminInvoices     from './admin/AdminInvoices';
import AdminSocial       from './admin/AdminSocial';
import AdminSEO          from './admin/AdminSEO';
import AdminUsers        from './admin/AdminUsers';
import AdminMaintenance  from './admin/AdminMaintenance';
import AdminMaintenanceItem from './admin/AdminMaintenanceItem';
import AdminMaintenanceMaterial from './admin/AdminMaintenanceMaterial';
import AdminSettings from './admin/AdminSettings';
import AdminTestimonials from './admin/AdminTestimonials';
import AdminBlog         from './admin/AdminBlog';
import AdminReviews      from './admin/AdminReviews';
import AdminFAQ          from './admin/AdminFAQ';

// Admin - CRM
import AdminLeads            from './admin/AdminLeads';
import AdminLeadDetail       from './admin/AdminLeadDetail';
import AdminPipeline         from './admin/AdminPipeline';
import AdminLeadAnalytics    from './admin/AdminLeadAnalytics';
import AdminReminders        from './admin/AdminReminders';
import AdminQuotationBuilder from './admin/AdminQuotationBuilder';
import { AdminQuotationList, AdminQuotationForm } from './admin/AdminQuotation';
import QuotationPreview from './admin/QuotationPreview';
import InvoicePreview from './admin/InvoicePreview';
import { Gallery, Offers } from './pages/_pages';
import { AdminGallery, AdminOffers } from './admin/_all_admin';

const Protected = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/admin/login" replace />;
};

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
    <PWAInstallBanner />
    <WhatsAppFloat />
    <ScrollTop />
    <MobileBottomNav />   {/* ← ADD THIS — shows only on mobile */}
  </>
);

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public */}
        <Route path="/"                                     element={<PublicLayout><Home /></PublicLayout>} />
        <Route path="/about"                                element={<PublicLayout><About /></PublicLayout>} />
        <Route path="/services"                             element={<PublicLayout><Services /></PublicLayout>} />
        <Route path="/services/complete-modular-site-work" element={<PublicLayout><CompleteModular /></PublicLayout>} />
        <Route path="/services/interior-design"            element={<PublicLayout><InteriorDesign /></PublicLayout>} />
        <Route path="/services/home-lighting"              element={<PublicLayout><HomeLighting /></PublicLayout>} />
        <Route path="/services/modular-kitchen"            element={<PublicLayout><ModularKitchen /></PublicLayout>} />
        <Route path="/services/false-ceiling-painting"     element={<PublicLayout><FalseCeiling /></PublicLayout>} />
        <Route path="/services/aluminium-glass-works"      element={<PublicLayout><AluminiumGlass /></PublicLayout>} />
        <Route path="/portfolio"                           element={<PublicLayout><Portfolio /></PublicLayout>} />
        <Route path="/gallery"                             element={<PublicLayout><Gallery /></PublicLayout>} />
        <Route path="/offers"                              element={<PublicLayout><Offers /></PublicLayout>} />
        <Route path="/careers"                             element={<PublicLayout><Careers /></PublicLayout>} />
        <Route path="/contact"                             element={<PublicLayout><Contact /></PublicLayout>} />
        <Route path="/get-free-quote"                      element={<PublicLayout><GetQuote /></PublicLayout>} />
        <Route path="/reviews"                             element={<PublicLayout><Reviews /></PublicLayout>} />
        <Route path="/faq"                                 element={<PublicLayout><FAQ /></PublicLayout>} />
        <Route path="/blog"                                element={<PublicLayout><Blog /></PublicLayout>} />
        <Route path="/blog/:slug"                          element={<PublicLayout><BlogPost /></PublicLayout>} />
        <Route path="/knowledge-hub"                       element={<Navigate to="/blog" replace />} />
        <Route path="/privacy-policy"                      element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
        <Route path="/terms-and-conditions"                element={<PublicLayout><TermsAndConditions /></PublicLayout>} />
        <Route path="/sitemap"                             element={<PublicLayout><Sitemap /></PublicLayout>} />

        {/* Admin */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
          <Route index                    element={<AdminDashboard />} />
          <Route path="banners"           element={<AdminBanners />} />
          <Route path="portfolio"         element={<AdminPortfolio />} />
          <Route path="careers"           element={<AdminCareers />} />
           <Route path="gallery"           element={<AdminGallery />} />
            <Route path="Offers"           element={<AdminOffers />} />
          <Route path="contacts"          element={<AdminContacts />} />
          <Route path="quotation"         element={<AdminQuotationList />} />
          <Route path="quotation/new"     element={<AdminQuotationForm />} />
          <Route path="quotation/:id"     element={<AdminQuotationForm />} />
          <Route path="quotation/:id/preview" element={<QuotationPreview />} />
          <Route path="invoice"           element={<AdminInvoices />} />
          <Route path="invoice/:id/preview" element={<InvoicePreview />} />
          <Route path="social"            element={<AdminSocial />} />
          <Route path="seo"               element={<AdminSEO />} />
          <Route path="users"             element={<AdminUsers />} />
          <Route path="maintenance"       element={<AdminMaintenance />} />
          <Route path="maintenance/item"  element={<AdminMaintenanceItem />} />
          <Route path="maintenance/material" element={<AdminMaintenanceMaterial />} />
          <Route path="settings"          element={<AdminSettings />} />
          <Route path="testimonials"      element={<AdminTestimonials />} />
          <Route path="blog"              element={<AdminBlog />} />
          <Route path="reviews"           element={<AdminReviews />} />
          <Route path="faq"               element={<AdminFAQ />} />
          {/* CRM */}
          <Route path="crm/leads"                    element={<AdminLeads />} />
          <Route path="crm/leads/:id"                element={<AdminLeadDetail />} />
          <Route path="crm/pipeline"                 element={<AdminPipeline />} />
          <Route path="crm/analytics"                element={<AdminLeadAnalytics />} />
          <Route path="crm/reminders"                element={<AdminReminders />} />
          <Route path="crm/quotations/new/:leadId"   element={<AdminQuotationBuilder />} />
          <Route path="crm/quotations/:id"           element={<AdminQuotationBuilder />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}